import { PropsWithChildren, useCallback, useState } from 'react';
import { Toaster as SonnerToaster, toast } from 'sonner';
import { CopyToClipboardToastProvider } from '../copy-to-clipboard-toast-provider';
import {
  NotificationContextValue,
  NotificationContext,
} from './NotificationContext';
import {
  TOAST_STACK_TRANSITION_MS,
  useNonErrorToasterTopOffset,
} from './use-non-error-toaster-offset';
import {
  createToast,
  ERROR_TOASTER_ID,
  NON_ERROR_TOASTER_ID,
  TOAST_RIGHT_OFFSET_PX,
  TOAST_TOP_OFFSET_PX,
} from './Notification.utils';

type NotificationProviderProps = PropsWithChildren<{
  isExtension?: boolean;
}>;

export const NotificationProvider = ({
  children,
  isExtension,
}: NotificationProviderProps) => {
  const [errorToasterEl, setErrorToasterEl] = useState<HTMLElement | null>(
    null
  );
  const nonErrorTopOffset = useNonErrorToasterTopOffset(errorToasterEl);

  const open: NotificationContextValue['open'] = useCallback(
    params => {
      if (!params) return;

      const { key, message, type, description, duration, action, closeAlign } =
        params;

      const opts = {
        description,
        id: key,
        isExtension,
        duration,
        action,
        closeAlign,
      };
      if (
        !['success', 'error', 'warning', 'info', 'alert', 'loading'].includes(
          type
        )
      )
        return;

      createToast(type)(message, opts);
    },
    [isExtension]
  );

  const close: NotificationContextValue['close'] = useCallback(
    key => toast.dismiss(key),
    []
  );

  return (
    <NotificationContext.Provider value={{ open, close, isExtension }}>
      <CopyToClipboardToastProvider>
        <SonnerToaster
          id={ERROR_TOASTER_ID}
          ref={setErrorToasterEl}
          position="top-right"
          offset={{
            top: `${TOAST_TOP_OFFSET_PX}px`,
            right: `${TOAST_RIGHT_OFFSET_PX}px`,
          }}
        />
        <SonnerToaster
          id={NON_ERROR_TOASTER_ID}
          position="top-right"
          offset={{
            top: nonErrorTopOffset,
            right: `${TOAST_RIGHT_OFFSET_PX}px`,
          }}
          style={{
            transition: `top ${TOAST_STACK_TRANSITION_MS}ms ease, transform 0.4s ease`,
          }}
        />
        {children}
      </CopyToClipboardToastProvider>
    </NotificationContext.Provider>
  );
};
